import React, { useState, useMemo, useEffect, useRef, Suspense, lazy } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { saveUserQuizAnswers } from '../../api/api';
import FunnelContext from './FunnelContext';
import funnelSteps from './funnelConfig';

import Header from '../../components/Header';
import Footer from '../../components/Footer';
import HeroSectionNew from './components/HeroSectionNew';

const QuestionPage = lazy(() => import('./pages/QuestionPage'));
const PromisePage = lazy(() => import('./pages/PromisePage'));
const TransitionPage = lazy(() => import('./pages/TransitionPage'));
const ReadinessPage = lazy(() => import('./pages/ReadinessPage'));
const ConfidentPage = lazy(() => import('./pages/ConfidentPage'));
const EmailCapturePage = lazy(() => import('./pages/EmailCapturePage'));
const CalculatingResults = lazy(() => import('./pages/CalculatingResults'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const SpecialOffer = lazy(() => import('./pages/SpecialOffer'));
const UpgradePage = lazy(() => import('./pages/UpgradePage'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));

const TermsAndConditions = lazy(() => import('../../pages/TermsAndConditions'));
const ContactPage = lazy(() => import('../../pages/ContactPage'));
const PrivacyPolicy = lazy(() => import('../../pages/PrivacyPolicy'));

const COMPONENT_MAP = {
  question: QuestionPage,
  promise: PromisePage,
  readiness: ReadinessPage,
  confident: ConfidentPage,
  calculating: CalculatingResults,
  email: EmailCapturePage,
  transition: TransitionPage,
  checkout: CheckoutPage,
  'special-offer': SpecialOffer,
  upgrade: UpgradePage,
  success: SuccessPage,
};

const progressSteps = funnelSteps.filter(s => s.showProgress);

function FunnelEngine({ basePath, funnelName, funnelVersion }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [answers, setAnswers] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('quizAnswers'));
      if (saved && typeof saved === 'object' && !Array.isArray(saved)) return saved;
    } catch { /* ignore */ }
    return {};
  });

  const relativePath = location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length).replace(/^\//, '')
    : '';

  const currentStepIndex = funnelSteps.findIndex(s => s.slug === relativePath);
  const currentStep = currentStepIndex >= 0 ? funnelSteps[currentStepIndex] : null;

  const currentProgressIndex = currentStep
    ? progressSteps.findIndex(s => s.slug === currentStep.slug)
    : -1;

  const progress = {
    current: currentProgressIndex >= 0 ? currentProgressIndex + 1 : 0,
    total: progressSteps.length,
    percentage:
      currentProgressIndex >= 0
        ? (currentProgressIndex / progressSteps.length) * 100
        : 0,
  };

  const prevStep = currentStepIndex > 0 ? funnelSteps[currentStepIndex - 1] : null;
  const prevPath = prevStep
    ? prevStep.slug === ''
      ? basePath || '/'
      : `${basePath}/${prevStep.slug}`
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const prevPathRef = useRef(null);
  useEffect(() => {
    if (!currentStep) return;
    const fullPath = currentStep.slug === '' ? basePath || '/' : `${basePath}/${currentStep.slug}`;
    if (fullPath === prevPathRef.current) return;
    prevPathRef.current = fullPath;

    const stepPayload = {
      event: 'dl_funnel_step',
      funnelName,
      funnelVersion,
      stepType: currentStep.type,
      stepSlug: currentStep.slug,
      stepIndex: currentStepIndex,
      stepPath: fullPath,
    };
    if (currentStep.type === 'question' && currentStep.data) {
      stepPayload.questionId = currentStep.data.id;
      stepPayload.questionText = currentStep.data.question;
    }
    TagManager.dataLayer({ dataLayer: stepPayload });
  }, [location.pathname, currentStep, currentStepIndex, basePath, funnelName, funnelVersion]);

  const navigateToStep = (step, options = {}) => {
    const path = step.slug === '' ? basePath || '/' : `${basePath}/${step.slug}`;
    navigate(path, options);
    window.scrollTo(0, 0);
  };

  const goNext = () => {
    if (currentStepIndex >= 0 && currentStepIndex < funnelSteps.length - 1) {
      navigateToStep(funnelSteps[currentStepIndex + 1]);
    }
  };

  const goPrev = () => {
    if (currentStepIndex > 0) {
      navigateToStep(funnelSteps[currentStepIndex - 1]);
    }
  };

  const goToSlug = (slug, options = {}) => {
    const step = funnelSteps.find(s => s.slug === slug);
    if (step) {
      navigateToStep(step, options);
    } else {
      navigate(`${basePath}/${slug}`, options);
      window.scrollTo(0, 0);
    }
  };

  const setAnswer = (id, value) => {
    const newAnswers = { ...answers, [id]: value };
    setAnswers(newAnswers);
    localStorage.setItem('quizAnswers', JSON.stringify(newAnswers));
  };

  const handleAnswer = (answer) => {
    if (!currentStep || currentStep.type !== 'question') return;

    const questionId = currentStep.data.id;
    const questionText = currentStep.data.question;

    setAnswer(questionId, answer);

    TagManager.dataLayer({
      dataLayer: {
        event: 'dl_quiz_answer',
        funnelName,
        funnelVersion,
        questionId,
        questionText,
        answer: typeof answer === 'string' ? answer : answer.join(', '),
        stepIndex: currentStepIndex,
      },
    });

    const userId = localStorage.getItem('userId');
    if (userId) {
      const apiAnswer = typeof answer === 'string' ? [answer] : answer;
      saveUserQuizAnswers(userId, questionText, apiAnswer, funnelName, funnelVersion);
    }
  };

  const contextValue = useMemo(
    () => ({
      goNext,
      goPrev,
      goToSlug,
      prevPath,
      progress,
      answers,
      setAnswer,
      handleAnswer,
      basePath,
      currentStep,
      currentStepIndex,
      funnelName,
      funnelVersion,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, answers]
  );

  const renderStep = (step) => {
    if (step.type === 'landing') {
      return <HeroSectionNew />;
    }

    const Component = COMPONENT_MAP[step.type];
    if (!Component) return <div>Unknown step type: {step.type}</div>;

    return <Component key={step.slug} />;
  };

  return (
    <FunnelContext.Provider value={contextValue}>
      <div className="ad2-funnel">
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          {funnelSteps.map((step) => (
            <Route
              key={step.slug || 'landing'}
              path={step.slug === '' ? '/' : `/${step.slug}`}
              element={renderStep(step)}
            />
          ))}

          <Route
            path="/terms"
            element={
              <>
                <Header />
                <div className="App__content"><TermsAndConditions /></div>
                <Footer />
              </>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <>
                <Header />
                <div className="App__content"><PrivacyPolicy /></div>
                <Footer />
              </>
            }
          />
          <Route
            path="/contact"
            element={
              <>
                <Header />
                <div className="App__content"><ContactPage /></div>
                <Footer />
              </>
            }
          />
        </Routes>
      </Suspense>
      </div>
    </FunnelContext.Provider>
  );
}

export default FunnelEngine;
